const ragFromIndex = (index) => {
  if (index >= 85) return "Green";
  if (index >= 70) return "Amber";
  return "Red";
};

const ragClass = (status) => {
  if (status === "Green") return "green";
  if (status === "Red") return "red";
  return "amber";
};

const formatBlockList = (blocks = []) => {
  if (!blocks.length) return "—";
  return blocks.map((b) => b.block_name).filter(Boolean).join(", ");
};

const pickAtRiskBlocks = (blockRankings, scoreKey, districtIndex, limit = 5) => {
  const threshold = Math.min(70, (districtIndex ?? 0) - 8);
  return [...(blockRankings || [])]
    .map((b) => ({ ...b, domainScore: b[scoreKey] ?? 0 }))
    .filter((b) => b.domainScore < threshold || b.domainScore < (districtIndex ?? 100) - 10)
    .sort((a, b) => a.domainScore - b.domainScore)
    .slice(0, limit);
};

const fallbackLowBlocks = (blockRankings, scoreKey, limit = 5) =>
  [...(blockRankings || [])]
    .sort((a, b) => (a[scoreKey] ?? 0) - (b[scoreKey] ?? 0))
    .slice(0, limit);

const identityProblem = (domain) => {
  const index = domain.index ?? 0;
  const aadhaar = domain.aadhaar_pct ?? 0;
  const apaar = domain.apaar_pct ?? 0;
  const gap = Math.round((aadhaar - apaar) * 10) / 10;
  if (apaar < 85 && gap >= 5) {
    return `APAAR completion (${apaar}%) trails Aadhaar (${aadhaar}%) by ${gap} pts — student identity records remain incomplete.`;
  }
  if (index < 70) {
    return `Identity compliance index is ${index}, below the 70-point intervention threshold.`;
  }
  if (index < 85) {
    return `Identity index is ${index}, short of the 85-point green benchmark despite reasonable Aadhaar coverage.`;
  }
  return "Identity compliance is stable; maintain APAAR–Aadhaar parity reviews in lower blocks.";
};

const infrastructureProblem = (domain) => {
  const index = domain.index ?? 0;
  const classroom = domain.classroom_health ?? 0;
  const toilet = domain.toilet_functional ?? 0;
  if (classroom < 90 && toilet < 90) {
    return `Classroom readiness (${classroom}%) and functional toilets (${toilet}%) both need remediation.`;
  }
  if (classroom < 92) {
    return `Classroom health at ${classroom}% — usable classroom stock is below district comfort band.`;
  }
  if (toilet < 95) {
    return `Functional toilet coverage at ${toilet}% — sanitation readiness gaps persist in weaker blocks.`;
  }
  if (index < 85) {
    return `Infrastructure index is ${index}, below the 85-point green benchmark.`;
  }
  return "Infrastructure is strong district-wide; focus preventive audits in outlier blocks.";
};

const teacherProblem = (domain) => {
  const index = domain.index ?? 0;
  const ctet = domain.ctet_pct ?? 0;
  if (ctet < 25) {
    return `CTET qualification rate is critically low at ${ctet}% — teacher quality is the largest OSH drag.`;
  }
  if (ctet < 50) {
    return `Only ${ctet}% of teachers are CTET-qualified; quality certification remains the primary weakness.`;
  }
  if (index < 70) {
    return `Teacher quality index is ${index}, pulling down the composite OSH score.`;
  }
  return "Teacher quality is acceptable; sustain CTET coaching in blocks below district average.";
};

const operationalProblem = (domain) => {
  const index = domain.index ?? 0;
  const completion = domain.completion_rate ?? 0;
  const certification = domain.certification_rate ?? 0;
  if (completion >= 90 && certification < 50) {
    return `Data entry is ${completion}% complete but only ${certification}% certified — approval workflow is the bottleneck.`;
  }
  if (certification < 30) {
    return `Certification rate is ${certification}% — officer sign-off is blocking operational readiness.`;
  }
  if (index < 72) {
    return `Operational index is ${index}; completion and certification need coordinated block follow-up.`;
  }
  return "Operational performance is stable; monitor certification lag in amber blocks.";
};

const DOMAIN_CONFIG = [
  {
    id: "identity",
    label: "Student Overview",
    tabId: "identity",
    scoreKey: "identity_score",
    problem: identityProblem,
    resolution: (domain, blocks) => {
      const blockText = formatBlockList(blocks);
      const apaar = domain.apaar_pct ?? 0;
      if (apaar < 90) {
        return `Run a 30-day APAAR closure sprint in ${blockText || "priority blocks"}; pair school heads with data-entry teams for daily APAAR generation targets.`;
      }
      return `Validate Aadhaar–APAAR name-match exceptions in ${blockText || "priority blocks"} and clear pending student identity records weekly.`;
    },
  },
  {
    id: "infrastructure",
    label: "Infrastructure Overview",
    tabId: "infrastructure",
    scoreKey: "infra_score",
    problem: infrastructureProblem,
    resolution: (domain, blocks) => {
      const blockText = formatBlockList(blocks);
      const classroom = domain.classroom_health ?? 0;
      if (classroom < 92) {
        return `Prioritize classroom repair and usability verification in ${blockText || "priority blocks"}; escalate non-functional rooms within 15 days.`;
      }
      return `Schedule toilet and water-point functional audits in ${blockText || "priority blocks"}; close sanitation gaps before the next review cycle.`;
    },
  },
  {
    id: "teacher",
    label: "Teacher Overview",
    tabId: "teacher",
    scoreKey: "teacher_score",
    problem: teacherProblem,
    resolution: (domain, blocks) => {
      const blockText = formatBlockList(blocks);
      return `Launch a teacher-quality improvement sprint in ${blockText || "priority blocks"} with CTET coaching, vacancy fill plans and weekly progress reviews until CTET crosses 50%.`;
    },
  },
  {
    id: "operational",
    label: "Operational Overview",
    tabId: "operational",
    scoreKey: "ops_score",
    problem: operationalProblem,
    resolution: (domain, blocks) => {
      const blockText = formatBlockList(blocks);
      const certification = domain.certification_rate ?? 0;
      if (certification < 50) {
        return `Assign block officers to daily certification sign-off in ${blockText || "priority blocks"}; clear the approval backlog within 30 days.`;
      }
      return `Accelerate data certification workflows in ${blockText || "priority blocks"} and link completion dashboards to officer accountability.`;
    },
  },
];

/**
 * Build four OSH priority-domain rows for the executive overview modal.
 */
export function buildPriorityDomainRows(domains = {}, blockRankings = []) {
  return DOMAIN_CONFIG.map((cfg) => {
    const domain = domains[cfg.id] ?? {};
    const index = domain.index ?? 0;
    const status = ragFromIndex(index);
    let atRisk = pickAtRiskBlocks(blockRankings, cfg.scoreKey, index);
    if (!atRisk.length) {
      atRisk = fallbackLowBlocks(blockRankings, cfg.scoreKey, 3);
    }
    return {
      id: cfg.id,
      label: cfg.label,
      tabId: cfg.tabId,
      scoreKey: cfg.scoreKey,
      index,
      status,
      statusClass: ragClass(status),
      problem: cfg.problem(domain),
      atRiskBlocks: atRisk,
      atRiskBlockNames: formatBlockList(atRisk),
      resolution: cfg.resolution(domain, atRisk),
    };
  });
}
